import React, { ChangeEvent, ReactNode, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Grid, TextField, Typography, FormControl, InputAdornment, MenuItem, Select, SelectChangeEvent, TableCell, TableRow, TableContainer, Table, TableHead, TableBody, TableSortLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, SvgIcon, Box, useMediaQuery, Paper, Stack } from '@mui/material';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { theme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import CustomPagination from '../../../components/forms/Pagination';
import { useNavigate } from 'react-router-dom';
import { StockCheckStatus, StockCheck, TransportCost } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import { StockCheckStatus as StockCheckStatusEnum } from '../../../enums/StockCheckStatus';
import { ActiveTab } from '../../../enums/ActiveTab';
import { SearchSm } from '../../../assets';
import { Edit } from '@mui/icons-material';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';

interface TabState {
  activeTab: ActiveTab;
  refreshKey?: number; 
}

const TransportCostPage: React.FC<TabState> = ({ activeTab, refreshKey }) => {
  
  const { t } = useTranslation('common');
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 10;
  const [selectedDate, setSelectedDate] = useState<string>();

  const [data, setData] = useState<TransportCost[]>([]);
  const filteredData = selectedDate
    ? data.filter((row) => dayjs(row.startDate).isSame(dayjs(selectedDate), 'day'))
    : data;
  const pagedData = data.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const [count, setCount] = useState<number | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<StockCheckStatus>(0);
  const { getTransportCosts } = useNswagClient();
  const { getTransportCostById } = useNswagClient();
  const { updateTransportCost } = useNswagClient();
  const { deleteStockCheck } = useNswagClient();
  const navigate = useNavigate();
  const [currentRowId, setCurrentRowId] = useState<number>(0);
  const [showAlert, setShowAlert] = useState(false);
  const [open, setOpen] = useState(false);
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState<boolean>(true);
  const [currentTab, setCurrentTab] = useState<boolean>(false);
  const [reset, setReset] = useState<boolean>(false);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string | Date | number>();
  const [editId, setEditId] = useState<number | null>(null);
  const [editTransportCost, setEditTransportCost] = useState<TransportCost | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errors, setErrors] = useState<{ expectedTransportCost?: string; actualTransportCost?: string }>({});
  const [editRowId, setEditRowId] = useState<number | null>(null);
  const [inlineEditData, setInlineEditData] = useState<TransportCost | null>(null);
  const [inlineErrors, setInlineErrors] = useState<{ expectedTransportCost?: string; actualTransportCost?: string }>({});


  useEffect(() => {
    getData();
  }, [page, currentTab, reset, refreshKey]);

  useEffect(() => {
    setLoading(true);
    if (activeTab === ActiveTab.Inactive) {
      setSelectedStatus(2);
      setCurrentTab(true);
    }
    if (activeTab === ActiveTab.Active) {
      setSelectedStatus(0);
      setCurrentTab(false);
    }
    setPage(0);
  },[activeTab]);
 
  const getData = () => {
    const storeNumber = selectedStore?.storeNumber;
    if (!storeNumber) return;

    setLoading(true);
    getTransportCosts(storeNumber).then((result) => {
      if (result?.data) {
        const now = dayjs();
        let filtered = result.data;

        if (activeTab === ActiveTab.Active) {
          filtered = filtered.filter((row) =>
            dayjs(row.startDate).isSame(now, 'month'),
          );
        } else if (activeTab === ActiveTab.Inactive) {
          filtered = filtered.filter((row) =>
            !dayjs(row.startDate).isSame(now, 'month'),
          );
        }

        setData(filtered);
        setCount(filtered.length);
      } else {
        console.error('Invalid response', result);
      }
      setLoading(false);
    }).catch((error) => {
      console.error('Error fetching transport costs:', error);
      setLoading(false);
    });
  };

  const handleEdit = async (event: React.MouseEvent<SVGSVGElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    setLoading(true);
    try {
      const result = await getTransportCostById(id);
      if (result?.data) {
        setEditTransportCost(result.data);
        setEditId(id);
        setDialogOpen(true);
      } else {
        console.error('Failed to load transport cost for editing');
      }
    } catch (error) {
      console.error('Error fetching transport cost:', error);
    }
    setLoading(false);
  };

  const searchStock = async () => {
    setPage(0);
    const storeNumber = selectedStore?.storeNumber;
    if (!storeNumber) return;

    setLoading(true);
    try {
      const result = await getTransportCosts(storeNumber);
      if (result?.data) {
        let filtered = result.data;
        const now = dayjs();

        // Apply selected date filter
        if (selectedDate) {
          filtered = filtered.filter(row =>
            dayjs(row.startDate).isSame(dayjs(selectedDate), 'day'),
          );
        }

        // Apply tab-specific filter
        if (activeTab === ActiveTab.Active) {
          filtered = filtered.filter(row =>
            dayjs(row.startDate).isSame(now, 'month'),
          );
        } else if (activeTab === ActiveTab.Inactive) {
          filtered = filtered.filter(row =>
            !dayjs(row.startDate).isSame(now, 'month'),
          );
        }

        setData(filtered);
        setCount(filtered.length);
      }
    } catch (error) {
      console.error('Error fetching transport costs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditTransportCost(null);
    setEditId(null);
  };

  const handleInputChange = (field: keyof TransportCost, value: string | number | null) => {
    if (!editTransportCost) return;

    const newErrors = { ...errors };

    if (field === 'expectedTransportCost') {
      if (value === '' || value === undefined || value === null || Number(value) < 0) {
        newErrors.expectedTransportCost = 'Expected transport cost is required and cannot be negative';
      } else {
        delete newErrors.expectedTransportCost;
      }
    }

    if (field === 'actualTransportCost') {
      if (value !== undefined && value !== '' && Number(value) < 0) {
        newErrors.actualTransportCost = 'Actual transport cost cannot be negative';
      } else {
        delete newErrors.actualTransportCost;
      }
    }

    setErrors(newErrors);

    setEditTransportCost({
      ...editTransportCost,
      [field]: value,
    });
  };

  const toNumberOrUndefined = (value: string | number | undefined | null) => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string') {
      if (value.trim() === '') return undefined;
      const n = Number(value);
      return isNaN(n) ? undefined : n;
    }
    if (typeof value === 'number') {
      return isNaN(value) ? undefined : value;
    }
    return undefined;
  };

  const handleSave = async () => {
    if (!editTransportCost || editId === null) return;

    try {
      const actualCostValue = toNumberOrUndefined(editTransportCost.actualTransportCost);

      await updateTransportCost(editId, {
        ...editTransportCost,
        expectedTransportCost: Number(editTransportCost.expectedTransportCost),
        actualTransportCost: actualCostValue,
        startDate: dayjs(editTransportCost.startDate).format('YYYY-MM-DD'),
      });

      setDialogOpen(false);
      setEditTransportCost(null);
      setEditId(null);
      getData(); // refresh list
    } catch (error) {
      console.error('Failed to update transport cost:', error);
    }
  };

  const comparator = (property: keyof TransportCost) => (a: TransportCost, b: TransportCost): number => {
    const isAsc = orderBy === property && order === 'asc';
    const aValue = a[property];
    const bValue = b[property];

    if (typeof aValue === 'string' || aValue instanceof Date) {
      return isAsc
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    } else {
      return isAsc ? Number(aValue) - Number(bValue) : Number(bValue) - Number(aValue);
    }
  };

  const toSort = (arr: TransportCost[], compareFn: (a: TransportCost, b: TransportCost) => number): TransportCost[] => {
    return arr.slice().sort(compareFn);
  };

  const handleSort = (property: keyof TransportCost) => {
    const isAsc = orderBy === property && order === 'asc';
    const sortedData = toSort(data, comparator(property));
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setData(sortedData);
  };

  const handleDateChange = (dateString: string | null) => {
    if(dateString)
    {
      setSelectedDate(dateString);
    }
  };

  const clearAll = () => {
    setPage(0);
    setSelectedDate('');
    setSelectedStatus(activeTab === ActiveTab.Inactive ? StockCheckStatusEnum.Completed : 0);
    setReset(!reset);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>, row: TransportCost) => {
    if (!hasPermissionTo(['FinancialManagementWrite'])) return;
    // Don't trigger row edit if already editing this row
    if (editRowId === row.id) return;

    // Only allow editing if startDate is in the current month
    const rowDate = dayjs(row.startDate);
    const now = dayjs();
    if (!rowDate.isSame(now, 'month')) return;

    setEditRowId(row.id ?? null);
    setInlineEditData({ ...row });
  };

  const handleInlineChange = (field: keyof TransportCost, rawValue: string | number) => {
    if (!inlineEditData) return;

    const value = typeof rawValue === 'string' ? rawValue : rawValue.toString();
    const newErrors = { ...inlineErrors };

    // Only validate if value is a parseable number
    const parsed = parseFloat(value);

    if (field === 'expectedTransportCost' || field === 'actualTransportCost') {
      if (value.trim() === '') {
        newErrors[field] = 'Required';
      } else if (!isNaN(parsed) && parsed < 0) {
        newErrors[field] = 'Value must be 0 or greater';
      } else {
        delete newErrors[field];
      }
    }

    setInlineErrors(newErrors);

    setInlineEditData((prev) => ({
      ...prev!,
      [field]: value,
    }));
  };

  const handleInlineSave = async () => {
    if (!inlineEditData || editRowId === null) return;

    try {
      const actualCostValue = toNumberOrUndefined(inlineEditData.actualTransportCost);
      await updateTransportCost(editRowId, {
        ...inlineEditData,
        expectedTransportCost: Number(inlineEditData.expectedTransportCost),
        actualTransportCost: actualCostValue,
        startDate: dayjs(inlineEditData.startDate).format('YYYY-MM-DD'),
      });

      setEditRowId(null);
      setInlineEditData(null);
      getData(); // refresh list
    } catch (error) {
      console.error('Failed to update transport cost:', error);
    }
  };

  const handleInlineCancel = () => {
    setEditRowId(null);
    setInlineEditData(null);
  };

  const maxPages = () => count ? Math.ceil(count / rowsPerPage) : 1;

  return (
    <Grid container 
      sx={{ padding: '24px 12px' }}
    >
      <Grid
        container
        spacing={2}
        display='flex'
        pb='2em'
        flexWrap={isMobile ? 'wrap' : 'nowrap'}
        alignItems='self-end'
      >
        <Grid item
          md={4}
          sm={12}
          xs={12}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', pb: isMobile ? 4 : 0 }}>
          <DatePickerLocalized
            label={t('transportCosts.startDate')}
            useCalendar={false}
            value={selectedDate}
            onChange={handleDateChange}
            disableHighlightToday={true}
            translations={{
              clearButtonLabel: t('calendar.clearButtonLabel'),
              todayButtonLabel: t('calendar.todayButtonLabel'),
              cancelButtonLabel: t('calendar.cancelButtonLabel'),
              okButtonLabel: t('calendar.okButtonLabel'),
            }}
          />
        </Grid>
        <Grid container
          sx={{ display: 'flex', alignItems: 'right', justifyContent: 'flex-end' }}>
          <Grid item
            md={2}
            xs={12}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right', pb: isMobile ? 4 : 0  }}>
            <Button
              variant="secondary"
              size="lg"
              onClick={clearAll}
              sx={{ px: 10, mr: isMobile ? 0 : 4 }}
            >
              {t('clearAll')}
            </Button>
          </Grid>
          <Grid item
            md={2}
            xs={12}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right' }}>
            <Button
              variant="primary"
              size="lg"
              onClick={searchStock}
              startIcon={<SearchSm />}
              sx={{ px: 10 }}
            >
              {t('search')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {showAlert && (
        <Alert severity="success">{t('stockcounts.successalert')}</Alert>
      )}
      <Grid container
        item
        xs={12}>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {t('stockcounts.confirmation')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t('stockcounts.message')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="secondary"
              onClick={handleClose}> {t('stockcounts.cancel')}</Button>
            <Button
              variant="bad"
              startIcon={<SvgIcon><Edit /></SvgIcon>}>
              {t('stockcounts.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pr: '48px' }}
            fontFamily={'SoDoSans'}
            fontSize={'18px'}
            fontWeight={600}>
            {editId ? t('transportCosts.edit') : t('transportCosts.create')}
          </DialogTitle>
          <DialogContent>
            {editTransportCost ? (
              <Grid container
                spacing={2}>
                <Grid item
                  xs={12}>
                  <DatePickerLocalized
                    label={t('transportCosts.startDate') + ' *'}
                    useCalendar={false}
                    value={editTransportCost.startDate ? dayjs(editTransportCost.startDate).format('YYYY-MM-DD') : ''}
                    onChange={(e: string | null) => handleInputChange('startDate', e)}
                    shouldDisableDate={(date: string) => dayjs(date).day() !== 1 }
                    disableHighlightToday={true}
                    translations={{
                      clearButtonLabel: t('calendar.clearButtonLabel'),
                      todayButtonLabel: t('calendar.todayButtonLabel'),
                      cancelButtonLabel: t('calendar.cancelButtonLabel'),
                      okButtonLabel: t('calendar.okButtonLabel'),
                    }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sx={{ mt: 5 }}>
                  <TextField
                    label={t('transportCosts.expected')}
                    type="number"
                    inputProps={{ min: 0 }}
                    fullWidth
                    required={true}
                    value={editTransportCost.expectedTransportCost ?? ''}
                    onChange={(e) => handleInputChange('expectedTransportCost', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    error={Boolean(errors.expectedTransportCost)}
                    helperText={errors.expectedTransportCost}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sx={{ mt: 5 }}>
                  <TextField
                    label={t('transportCosts.actual')}
                    type="number"
                    inputProps={{ min: 0 }}
                    fullWidth
                    value={editTransportCost.actualTransportCost ?? ''}
                    onChange={(e) => handleInputChange('actualTransportCost', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    error={Boolean(errors.actualTransportCost)}
                    helperText={errors.actualTransportCost}
                  />
                </Grid>
              </Grid>
            ) : (
              <LoadingWheel />
            )}
          </DialogContent>
          <DialogActions>

            <Grid container
              spacing={4}
              justifyContent="space-between">
              
              <Grid item
                xs={12}>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={4}>
                  <Button variant="secondary"
                    size="lg"
                    onClick={handleDialogClose}>
                    {t('cancel')}
                  </Button>
                  <Button variant="primary"
                    size="lg"
                    onClick={handleSave}
                    disabled={
                      !editTransportCost ||
                                    Boolean(errors.expectedTransportCost) ||
                                    Boolean(errors.actualTransportCost) ||
                                    editTransportCost.expectedTransportCost === undefined
                    }
                  >
                    {t('save')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            
          </DialogActions>
        </Dialog>
        {loading ? <LoadingWheel /> :
          <Grid
            sx={{ width: '100%' }}>
            <TableContainer component={'div'}
            >
              <Table
                size="small"
                aria-label="a table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy === 'startDate' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'startDate'}
                        direction={orderBy === 'startDate' ? order : 'asc'}
                        onClick={() => handleSort('startDate')}
                      >
                        {t('transportCosts.timeframe')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'expectedTransportCost' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'expectedTransportCost'}
                        direction={orderBy === 'expectedTransportCost' ? order : 'asc'}
                        onClick={() => handleSort('expectedTransportCost')}
                      >
                        {t('transportCosts.expected')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy === 'actualTransportCost' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'actualTransportCost'}
                        direction={orderBy === 'actualTransportCost' ? order : 'asc'}
                        onClick={() => handleSort('actualTransportCost')}
                      >
                        {t('transportCosts.actual')}
                      </TableSortLabel>
                    </TableCell>
                    {
                      hasPermissionTo(['FinancialManagementWrite']) &&
                      <TableCell>
                        {t('transportCosts.actions')}
                      </TableCell>
                    }
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedData.map((row: TransportCost) => {
                    const isEditing = editRowId === row.id;

                    return (
                      <TableRow
                        hover
                        key={row.id}
                        onClick={(e) => handleRowClick(e, row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          {`${dayjs(row.startDate).format('DD/MM/YYYY')} - ${dayjs(row.startDate).add(6, 'day').format('DD/MM/YYYY')}`}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              type="number"
                              inputProps={{ min: 0 }}
                              error={Boolean(inlineErrors.expectedTransportCost)}
                              helperText={inlineErrors.expectedTransportCost || ''}
                              value={inlineEditData?.expectedTransportCost ?? ''}
                              onChange={(e) => handleInlineChange('expectedTransportCost', parseFloat(e.target.value))}
                            />
                          ) : (
                            row.expectedTransportCost
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              type="number"
                              inputProps={{ min: 0 }}
                              value={inlineEditData?.actualTransportCost ?? ''}
                              error={Boolean(inlineErrors.actualTransportCost)}
                              helperText={inlineErrors.actualTransportCost || ''}
                              onChange={(e) => handleInlineChange('actualTransportCost', parseFloat(e.target.value))}
                            />
                          ) : (
                            row.actualTransportCost
                          )}
                        </TableCell>
                        {hasPermissionTo(['FinancialManagementWrite']) && (
                          <TableCell>
                            {isEditing ? (
                              <>
                                <Button variant="secondary"
                                  size="lg"
                                  sx={{ mr: 2 }}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click
                                    handleInlineCancel();
                                  }}
                                >
                                  {t('cancel')}
                                </Button>

                                <Button variant="primary"
                                  size="lg"
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click
                                    handleInlineSave();
                                  }}
                                >
                                  {t('save')}
                                </Button>
                              </>
                            ) : (
                              activeTab === ActiveTab.Active && (
                                <Edit
                                  sx={{ cursor: 'pointer', color: theme.palette.primary[500] }}
                                  onClick={(e) => handleEdit(e, row.id ?? 0)}
                                />
                              )
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {!count ? (
              <Box sx={{ pt: '20px', pb: '20px', display: 'flex', justifyContent: 'center' }}>
                <Typography variant="textSM">
                  {t('stockcounts.noData')}
                </Typography>
              </Box>
            ) : (
              <CustomPagination
                page={page}
                setPage={setPage}
                maxPages={maxPages()}
                breakpointForChangeDisplay={120000} />
            )}
          </Grid>}
      </Grid>
    </Grid>
  );
};
export default TransportCostPage;
